import { Box } from "@mui/material";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import {
  SITE_COLOR,
  SITE_MEDIA,
  SITE_WRAP,
  SITE_TOUCH_TARGET,
} from "src/domains/shared/constants/styles/site";
import { siteTelFor } from "../siteKit/siteTel";

/** 바닥 워드마크 글자 크기: 푸터 폭(양옆 여백 제외)을 글자 수에 맞춘 비율 — 시안 값 그대로 */
const WORDMARK_FIT_RATIO = 5.787;
const WORDMARK_SIDE = "clamp(12px,1.5vw,28px)";

const FOOTER_STYLES = {
  root: {
    position: "relative",
    overflow: "hidden",
    isolation: "isolate",
    containerType: "inline-size",
    background: SITE_COLOR.footerBg,
    borderTop: `1px solid ${SITE_COLOR.line2}`,
    paddingBlock: "clamp(56px,6vw,80px) clamp(110px,13vw,190px)",
    fontSize: "13px",
    lineHeight: 1.75,
    color: SITE_COLOR.ink2,
    userSelect: "text",
    "& *": { userSelect: "text" },
    // 배경 워드마크: 정가운데가 아니라 아래쪽에 반쯤 걸치게, 아주 옅게
    "&::before": {
      content: '"DroneSquare"',
      // 장식 글자 — 가짜 요소 글자는 스크린 리더가 읽으므로 대체 글을 비운다(이 문법을 모르는 브라우저는 위 값만 쓴다)
      '@supports (content: "x" / "")': { content: '"DroneSquare" / ""' },
      position: "absolute",
      zIndex: -1,
      left: "50%",
      transform: "translateX(-50%)",
      bottom: "-.16em",
      fontSize: `calc((100vw - 2 * ${WORDMARK_SIDE}) / ${WORDMARK_FIT_RATIO})`,
      "@supports (width: 1cqw)": {
        fontSize: `calc((100cqw - 2 * ${WORDMARK_SIDE}) / ${WORDMARK_FIT_RATIO})`,
      },
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: "-.045em",
      color: "rgba(15,23,42,.045)",
      whiteSpace: "nowrap",
      pointerEvents: "none",
      userSelect: "none",
      [SITE_MEDIA.tablet]: { bottom: "-.04em" },
    },
  },
  wrap: SITE_WRAP,
  grid: {
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr) auto",
    gap: "28px 48px",
    [SITE_MEDIA.tablet]: { gridTemplateColumns: "1fr" },
  },
  left: { display: "flex", flexDirection: "column", gap: "24px" },
  // 링크는 누르는 영역을 44px 까지 넓히고, 줄이 바뀌어도 넓힌 영역끼리 겹치지 않게 줄 사이를 18px 둔다
  menu: {
    display: "flex",
    flexWrap: "wrap",
    gap: "18px 22px",
    "& a": SITE_TOUCH_TARGET,
    fontSize: "14px",
    fontWeight: 600,
    color: SITE_COLOR.ink,
    "& [data-dim]": { color: SITE_COLOR.ink2 },
  },
  company: { "& b": { color: SITE_COLOR.ink, fontWeight: 600 } },
  contact: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: "12px",
    textAlign: "right",
    [SITE_MEDIA.tablet]: { alignItems: "flex-start", textAlign: "left" },
  },
  tel: {
    display: "flex",
    flexDirection: "column",
    alignItems: "inherit",
    "& [data-label]": { lineHeight: "24.5px" },
    "& [data-tel]": {
      fontSize: "22px",
      lineHeight: 1.3,
      fontWeight: 600,
      color: SITE_COLOR.ink,
      fontVariantNumeric: "tabular-nums",
    },
  },
  bottom: {
    display: "flex",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap",
    marginTop: "40px",
    color: SITE_COLOR.ink3,
    fontSize: "12px",
  },
} as const;

/**
 * 사이트 푸터(marketing-site-shell): 메뉴 줄·회사 정보·대표전화·저작권, 바닥에 옅은 워드마크(장식, CSS 가짜 요소).
 * 대상 페이지가 없는 「회사소개」·「이용약관」 은 링크로 만들지 않는다(privacy-policy-page).
 */
export function SiteFooter() {
  const { t, i18n } = useTranslation("landing");
  return (
    <Box component="footer" sx={FOOTER_STYLES.root}>
      <Box sx={FOOTER_STYLES.wrap}>
        <Box sx={FOOTER_STYLES.grid}>
          <Box sx={FOOTER_STYLES.left}>
            <Box
              component="nav"
              sx={FOOTER_STYLES.menu}
              aria-label={t("site.footer.menuLabel")}
            >
              <span data-dim="">{t("site.footer.company")}</span>
              <span data-dim="">{t("site.footer.terms")}</span>
              <Link href="/privacy">{t("site.footer.privacy")}</Link>
              <Link href="/inquiry" data-dim="">
                {t("site.footer.contact")}
              </Link>
            </Box>
            <Box sx={FOOTER_STYLES.company}>
              <b>{t("site.footer.companyName")}</b>
              <br />
              {t("site.footer.businessNumber")}
              <br />
              {t("site.footer.address")}
            </Box>
          </Box>
          <Box sx={FOOTER_STYLES.contact}>
            <Box sx={FOOTER_STYLES.tel}>
              <span data-label="">{t("site.footer.telLabel")}</span>
              <span data-tel="">{siteTelFor(i18n.language)}</span>
            </Box>
            <div>
              {t("site.footer.hours")}
              <br />
              {t("site.footer.closed")}
            </div>
          </Box>
        </Box>
        <Box sx={FOOTER_STYLES.bottom}>
          <span>{t("site.footer.copyright")}</span>
        </Box>
      </Box>
    </Box>
  );
}
